// "Open as this company" (CR_20260913 §4.2 step 5): reason (S2) + duration (8 h default = cap, S1) + the role the
// visit acts as and its reach (CR_20260924 Part C), with the notice that the customer's managers see the visit (S3).
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { actIsValid, MANAGER_ACT, type SupportAct } from '@fleetspoke/core/support';
import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useSession } from '../auth/session';
import { SupportActFields } from './SupportActFields';

const DURATIONS = [60, 240, 480] as const;

export function StartSupportDialog({
  company,
  onClose,
}: {
  company: { company_id: string; name: string };
  onClose(): void;
}) {
  const { t } = useTranslation();
  const { startSupport } = useSession();
  const navigate = useNavigate();
  const [reason, setReason] = useState('');
  const [minutes, setMinutes] = useState<number>(480);
  const [act, setAct] = useState<SupportAct>(MANAGER_ACT);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const valid = reason.trim().length >= 5 && actIsValid(act);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!valid) return;
    setBusy(true);
    setError(null);
    try {
      await startSupport(company.company_id, reason.trim(), minutes, act);
      onClose();
      void navigate('/', { replace: true });
    } catch (err) {
      const code = (err as { code?: string }).code;
      setError(
        code === '55000'
          ? t('support.err_inactive')
          : code === '22023'
            ? t('support.err_reason')
            : code === '42501'
              ? t('common.not_allowed')
              : t('common.error_generic'),
      );
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <form onSubmit={(e) => void submit(e)}>
        <DialogTitle>{t('support.start_title', { company: company.name })}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <TextField
              label={t('support.reason')}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              helperText={t('support.reason_help')}
              required
              autoFocus
              multiline
              minRows={2}
              slotProps={{ htmlInput: { maxLength: 500 } }}
            />
            <TextField
              select
              label={t('support.duration')}
              value={minutes}
              onChange={(e) => setMinutes(Number(e.target.value))}
              sx={{ maxWidth: 240 }}
            >
              {DURATIONS.map((m) => (
                <MenuItem key={m} value={m}>
                  {t(`support.duration_${m}`)}
                </MenuItem>
              ))}
            </TextField>
            <SupportActFields
              companyId={company.company_id}
              value={act}
              onChange={setAct}
              disabled={busy}
            />
            <Alert severity="info">
              <Typography variant="body2">
                {t('support.notice', { company: company.name })}
              </Typography>
            </Alert>
            {error ? <Alert severity="error">{error}</Alert> : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="contained" disabled={!valid || busy}>
            {t('support.start')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
